import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Edit, Download, FileText, Building, User, Calendar, CreditCard, Users, Briefcase } from 'lucide-react';
import './ContractView.css';

const url = process.env.REACT_APP_BACKEND_URL;

const ContractView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const response = await axios.get(`${url}/api/contract/${id}`);
        setContract(response.data.data);
      } catch (error) {
        toast.error('Failed to load contract details');
        navigate('/ViewContract');
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [id, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    if (!contract) return null;
    
    const today = new Date();
    const endDate = new Date(contract.contract_end);
    
    if (contract.status === 'inactive') {
      return <span className="status-badge inactive">Inactive</span>;
    }
    if (endDate < today) {
      return <span className="status-badge expired">Expired</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const getSectionBadge = (section) => {
    if (!section) return null;
    
    const sectionColors = {
      'Full Time': '#4caf50',
      'Part Time': '#ff9800',
      'Day': '#2196f3',
      'Night': '#9c27b0',
      'Morning': '#00bcd4',
      'Afternoon': '#ffc107'
    };
    
    return (
      <span className="section-badge" style={{ backgroundColor: sectionColors[section] || '#6c757d' }}>
        {section}
      </span>
    );
  };

  if (loading) return (
    <div className="contract-view-wrapper">
      <div className="loading-container">
        <div className="spinner-large"></div>
        <p>Loading contract details...</p>
      </div>
    </div>
  );
  
  if (!contract) return (
    <div className="contract-view-wrapper">
      <div className="error-container">
        <h3>Contract Not Found</h3>
        <p>The contract you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/ViewContract')} className="back-btn">
          Back to Contracts
        </button>
      </div>
    </div>
  );

  return (
    <div className="contract-view-wrapper">
      {selectedImage && (
        <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={`${url}${selectedImage}`} alt="Contract Document" />
            <a 
              href={`${url}${selectedImage}`} 
              download
              className="modal-download"
            >
              <Download size={16} /> Download
            </a>
          </div>
        </div>
      )}

      <div className="contract-view-container">
        {/* Header */}
        <div className="view-header">
          <button onClick={() => navigate('/ViewContract')} className="back-button">
            <ArrowLeft size={20} /> Back to Contracts
          </button>
          <div className="header-actions">
            <button onClick={() => navigate(`/contract/edit/${id}`)} className="edit-button">
              <Edit size={18} /> Edit Contract
            </button>
          </div>
        </div>

        {/* Title Section */}
        <div className="title-section">
          <div className="title-icon">
            <FileText size={32} />
          </div>
          <div className="title-content">
            <h1>Contract Details</h1>
            <div className="contract-id">
              Contract #{contract.no}
              {getStatusBadge()}
            </div>
          </div>
        </div>

        {/* Contract Information Card */}
        <div className="info-card">
          <h3>
            <FileText size={20} />
            Contract Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Contract Period:</label>
              <span>{formatDate(contract.contract_start)} - {formatDate(contract.contract_end)}</span>
            </div>
            <div className="info-item">
              <label>Registration Date:</label>
              <span>{formatDate(contract.regdate)}</span>
            </div>
            <div className="info-item full-width">
              <label>Status:</label>
              <span>{getStatusBadge()}</span>
            </div>
          </div>
        </div>

        {/* Customer Information Card */}
        <div className="info-card">
          <h3>
            <User size={20} />
            Customer Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name:</label>
              <span className="customer-name">{contract.cust_id?.fullname}</span>
            </div>
            <div className="info-item">
              <label>Phone:</label>
              <span>{contract.cust_id?.phone}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{contract.cust_id?.email}</span>
            </div>
            <div className="info-item full-width">
              <label>Address:</label>
              <span>{contract.cust_id?.address}</span>
            </div>
            <div className="info-item">
              <label>Registration Date:</label>
              <span>{formatDate(contract.cust_id?.date_reg)}</span>
            </div>
          </div>
        </div>

        {/* Client Information Card */}
        <div className="info-card">
          <h3>
            <Building size={20} />
            Client Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Client Name:</label>
              <span>{contract.client_id?.name}</span>
            </div>
            <div className="info-item">
              <label>Location:</label>
              <span>{contract.client_id?.location || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Manager:</label>
              <span>{contract.client_id?.manager_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Contact:</label>
              <span>{contract.client_id?.manager_contact || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Job Information Card */}
        <div className="info-card">
          <h3>
            <Briefcase size={20} />
            Job Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Job Category:</label>
              <span>{contract.job_id?.category_name}</span>
            </div>
            <div className="info-item">
              <label>Job Section:</label>
              <span>{contract.section ? getSectionBadge(contract.section) : 'N/A'}</span>
            </div>
            {contract.job_id?.description && (
              <div className="info-item full-width">
                <label>Job Description:</label>
                <span>{contract.job_id?.description}</span>
              </div>
            )}
          </div>
        </div>

        {/* Guarantor Information */}
        {(contract.damiinname || contract.damincontact) && (
          <div className="info-card">
            <h3>
              <Users size={20} />
              Guarantor Information
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{contract.damiinname || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Contact:</label>
                <span>{contract.damincontact || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Next of Kin Information */}
        {(contract.nokname || contract.nokcontact) && (
          <div className="info-card">
            <h3>
              <Users size={20} />
              Next of Kin Information
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{contract.nokname || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Contact:</label>
                <span>{contract.nokcontact || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Bank Information */}
        {(contract.bankname || contract.accountname || contract.accountno) && (
          <div className="info-card">
            <h3>
              <CreditCard size={20} />
              Bank Information
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Bank Name:</label>
                <span>{contract.bankname || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Account Name:</label>
                <span>{contract.accountname || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Account Number:</label>
                <span>{contract.accountno || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* CID/Letter Upload */}
        <div className="info-card">
          <h3>
            <FileText size={20} />
            CID/Letter Upload
          </h3>
          <div className="upload-section">
            {contract.cid_letter_upload ? (
              <div className="file-preview">
                <img 
                  src={`${url}${contract.cid_letter_upload}`} 
                  alt="CID/Letter" 
                  className="preview-image"
                  onClick={() => setSelectedImage(contract.cid_letter_upload)}
                />
                <div className="file-actions">
                  <a 
                    href={`${url}${contract.cid_letter_upload}`} 
                    download
                    className="download-link"
                  >
                    <Download size={16} /> Download Document
                  </a>
                </div>
              </div>
            ) : (
              <p className="no-file">No file uploaded</p>
            )}
          </div>
        </div>

        {/* Description */}
        {contract.description && (
          <div className="info-card">
            <h3>Description</h3>
            <div className="description-content">
              <p>{contract.description}</p>
            </div>
          </div>
        )}

        {/* System Information */}
        <div className="info-card">
          <h3>
            <Calendar size={20} />
            System Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Created By:</label>
              <span>{contract.createdBy?.name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Created At:</label>
              <span>{formatDateTime(contract.createdAt)}</span>
            </div>
            <div className="info-item">
              <label>Last Updated:</label>
              <span>{formatDateTime(contract.updatedAt)}</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="view-footer">
          <button onClick={() => navigate('/ViewContract')} className="secondary-btn">
            Back to List
          </button>
          <button onClick={() => navigate(`/contract/edit/${id}`)} className="primary-btn">
            <Edit size={18} /> Edit Contract
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractView;